import { useState } from "react";
import { ClipboardPaste, Copy, Loader2, PenLine, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { EditQuoteTemplateDialog } from "@/modules/quoteDocuments/EditQuoteTemplateDialog";
import {
  useQuoteDocumentTemplates,
  useQuoteDocumentTemplatesStore,
} from "@/modules/quoteDocuments/quoteDocumentTemplatesStore";
import { ImportQuoteTemplateSheet } from "@/modules/quoteDocuments/ImportQuoteTemplateSheet";
import { untitledTemplateName } from "@/modules/quoteDocuments/untitledTemplateName";

/** Select value standing in for "no organization default". */
const NO_DEFAULT_VALUE = "none";

interface DefaultQuoteTemplatePickerProps {
  /** The current org-default template id, or null when unset. */
  value: string | null;
  /** Called with the next template id, or null to clear the default. */
  onChange: (templateId: string | null) => void;
  /** Whether the control is disabled. */
  disabled?: boolean;
}

/**
 * Settings row picking the quote-document template new quotes start from, with
 * an entry point into the template editor for the selected template and a
 * button that creates an empty template and opens it there.
 * @param value - The current org-default template id, or null when unset
 * @param onChange - Called with the next template id, or null to clear it
 * @param disabled - Whether the control is disabled
 * @returns The default-template settings row element
 */
export function DefaultQuoteTemplatePicker({
  value,
  onChange,
  disabled = false,
}: DefaultQuoteTemplatePickerProps) {
  const templates = useQuoteDocumentTemplates();
  const createTemplate = useQuoteDocumentTemplatesStore(
    (state) => state.createTemplate,
  );
  const isCreating = useQuoteDocumentTemplatesStore(
    (state) => state.isLoadingCreate,
  );
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  );

  const [importOpen, setImportOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const fetchTemplate = useQuoteDocumentTemplatesStore(
    (state) => state.fetchTemplate,
  );

  const copyHtml = async () => {
    if (!value) {
      return;
    }
    setIsCopying(true);
    try {
      const template = await fetchTemplate(value);
      if (!template) {
        toast.error("Failed to load the template");
        return;
      }
      await navigator.clipboard.writeText(template.document);
      toast.success("Template copied");
    } catch {
      toast.error(
        "Could not copy template. Check your browser clipboard permissions.",
      );
    } finally {
      setIsCopying(false);
    }
  };

  const createBlankTemplate = async () => {
    const result = await createTemplate({
      name: untitledTemplateName(templates),
    });
    if (!result.template) {
      toast.error(result.error);
      return;
    }
    setEditingTemplateId(result.template.id);
  };

  return (
    <SettingsRow
      id="quote-settings-default-document-template"
      label="Default Quote Document Template"
      description="Applies to new quotes only; existing quotes are unchanged."
      expandedContent={
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={value ?? NO_DEFAULT_VALUE}
            onValueChange={(next) =>
              onChange(next === NO_DEFAULT_VALUE ? null : next)
            }
            disabled={disabled}
          >
            <SelectTrigger className="w-full sm:w-[280px]">
              <SelectValue placeholder="No default template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DEFAULT_VALUE}>
                No default template
              </SelectItem>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            className="text-muted-foreground"
            size="sm"
            disabled={disabled || value === null}
            onClick={() => setEditingTemplateId(value)}
            title={
              value === null
                ? "Select a template to edit it"
                : "Edit this template's document"
            }
          >
            <PenLine className="size-4" /> Edit
          </Button>
          <Button
            variant="ghost"
            className="text-muted-foreground"
            size="sm"
            disabled={disabled || isCreating}
            onClick={createBlankTemplate}
            title="Create an empty template and open it in the editor"
          >
            <Plus className="size-4" /> New template
            {isCreating && <Loader2 className="size-4 animate-spin" />}
          </Button>
          <Button
            variant="ghost"
            className="text-muted-foreground"
            size="sm"
            disabled={disabled || isCreating}
            onClick={() => setImportOpen(true)}
          >
            <ClipboardPaste className="size-4" /> Paste template
          </Button>
          <Button
            variant="ghost"
            className="text-muted-foreground"
            size="sm"
            disabled={disabled || value === null || isCopying}
            onClick={copyHtml}
          >
            <Copy className="size-4" /> Copy template
            {isCopying && <Loader2 className="size-4 animate-spin" />}
          </Button>
        </div>
      }
    >
      {importOpen && (
        <ImportQuoteTemplateSheet
          onClose={() => setImportOpen(false)}
          onCreated={setEditingTemplateId}
        />
      )}
      <EditQuoteTemplateDialog
        templateId={editingTemplateId}
        onClose={() => setEditingTemplateId(null)}
      />
    </SettingsRow>
  );
}
